import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Heart, Users, Sparkles, RotateCcw, Star } from 'lucide-react';
import { toast } from 'sonner';

function ConnectionCards({ onBack }) {
  const [currentCard, setCurrentCard] = useState(null);
  const [cardsDrawn, setCardsDrawn] = useState(0);
  const [favoriteCards, setFavoriteCards] = useState([]);
  const [cardType, setCardType] = useState('intimacy'); // 'intimacy', 'relational', 'gratitude'

  const intimacyQuestions = [
    "What's something you really value and appreciate about yourself?",
    "What makes you feel alive?", 
    "What do you really love about your life right now?",
    "What's something missing for you in your life?",
    "What's the most challenging thing you're dealing with right now?",
    "What's something about yourself you don't normally tell people you just met?",
    "When you were 10-11, what did you want to be when you grew up?",
    "What do you value most in a friendship?",
    "Who in your life are you most grateful for?",
    "What is the greatest pleasure in your life right now?",
    "What is one of the happiest memories of your life?",
    "What is something you did as a child that you now regret?",
    "If you could change one thing about your childhood, what would it be?",
    "What is something most people don't know about you?",
    "What is one of the proudest achievements of your life?",
    "What is one of the biggest disappointments of your life?",
    "What is something you're really good at?",
    "Is there someone you are holding a grudge against? Who? For what?",
    "What is something you wish your father had said to you?",
    "What is something you wish your mother had said to you?",
    "Have you ever had a big dream that changed you? What was it?",
    "If you woke up having one new quality or ability, what would you want it be?",
    "If you only had a year to live, what is one thing you would be sure to do?",
    "How have you changed this past year?",
    "If you died today, what would you most regret not having said or done?",
    "When do you feel most alive?",
    "What's one book you recommend and why?",
    "What does your 'perfect' day look like?",
    "What new habit could you develop that your future self would thank you for?",
    "What's one thing that really helps you when you're feeling down?",
    "Describe a moment in your life when you were most embarrassed."
  ];

  const relationalQuestions = [
    "What are you afraid will happen if you truly express yourself?",
    "What's something you're afraid to share with me? Why?", 
    "What do you most want to get out of this conversation?",
    "Why do you think we have met?",
    "What are three things that you think we have in common?",
    "What are you struggling with lately?",
    "How do you sense I can support you most?",
    "What's something you really want me to know about you? Why?",
    "What do you really want to know about me? Why?",
    "Name someone in the room who you are attracted to physically or otherwise.",
    "Name someone in the room you are curious about.",
    "What do you appreciate about how I'm listening to you right now?"
  ];

  const gratitudeQuestions = [
    "Tell me about a time someone made your life more wonderful",
    "What's something I've done that you're grateful for?",
    "What's something you've done recently that enriched someone else's life?",
    "Share a gratitude you've been wanting to express but haven't",
    "What's something in nature you're grateful for right now?",
    "Tell me about someone who shaped who you are today"
  ];

  const questionSets = {
    intimacy: { questions: intimacyQuestions, title: 'Intimacy Building', color: 'bg-pink-500', icon: Heart },
    relational: { questions: relationalQuestions, title: 'Relational Depth', color: 'bg-purple-500', icon: Users },
    gratitude: { questions: gratitudeQuestions, title: 'Gratitude Sharing', color: 'bg-yellow-500', icon: Star }
  };

  const drawCard = () => {
    const questions = questionSets[cardType].questions;
    const randomQuestion = questions[Math.floor(Math.random() * questions.length)];
    setCurrentCard(randomQuestion);
    setCardsDrawn(prev => prev + 1);
    
    // Play card draw sound
    const audio = new Audio('data:audio/wav;base64,UklGRj4AAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoAAAA4ODg4ODg4ODg4ODg4ODg4ODg4');
    audio.volume = 0.5;
    audio.play().catch(() => {});
    
    toast.success(`Card ${cardsDrawn + 1} drawn! 🎴`);
  };

  const addToFavorites = () => {
    if (currentCard && !favoriteCards.includes(currentCard)) {
      setFavoriteCards(prev => [...prev, currentCard]);
      toast.success('Added to favorites! ⭐');
    }
  };

  return (
    <div 
      className="min-h-screen"
      style={{
        backgroundImage: `url('https://customer-assets.emergentagent.com/job_belonging-sessions/artifacts/femlgvnm_ChatGPT%20Image%20Sep%2011%2C%202025%20at%2012_35_28%20AM.jpeg')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
    >
      <div className="min-h-screen bg-pink-900 bg-opacity-80 py-12 px-4">
        <div className="max-w-4xl mx-auto">
          
          {/* Header */}
          <div className="text-center mb-8">
            <Button onClick={onBack} variant="outline" className="border-white text-white hover:bg-white hover:text-black mb-6" style={{ borderRadius: 0 }}>
              ← Back to Games & Tools
            </Button>
            <h1 className="text-4xl font-bold text-white mb-4">💕 Connection Cards</h1>
            <p className="text-xl text-pink-200">Draw cards to deepen relationships and build authentic connection</p>
            
            <div className="flex justify-center gap-4 mt-6">
              <span className="bg-white text-pink-800 px-4 py-2 rounded font-bold">
                Cards Drawn: {cardsDrawn}
              </span>
              <span className="bg-yellow-500 text-black px-4 py-2 rounded font-bold">
                ⭐ Favorites: {favoriteCards.length}
              </span>
            </div>
          </div>

          {/* Card Type Selection */}
          <div className="grid md:grid-cols-3 gap-4 mb-8">
            {Object.entries(questionSets).map(([key, set]) => {
              const IconComponent = set.icon;
              return (
                <Button
                  key={key}
                  onClick={() => setCardType(key)}
                  className={`p-6 ${cardType === key ? set.color : 'bg-white'} ${
                    cardType === key ? 'text-white' : 'text-gray-800'
                  } hover:scale-105 transition-all`}
                  style={{ borderRadius: 0 }}
                >
                  <div className="space-y-2">
                    <IconComponent className="w-8 h-8 mx-auto" />
                    <p className="font-bold">{set.title}</p>
                    <p className="text-xs opacity-75">{questionSets[key].questions.length} cards</p>
                  </div>
                </Button>
              );
            })}
          </div>

          {/* Current Card */}
          <Card className="border-4 border-white bg-white mb-8" style={{ borderRadius: 0 }}>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle className="text-xl text-gray-800">
                  {questionSets[cardType].title} Card
                </CardTitle>
                <Button 
                  onClick={drawCard}
                  className={`${questionSets[cardType].color} text-white hover:opacity-90`}
                  style={{ borderRadius: 0 }}
                >
                  🎴 Draw Card
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {currentCard ? (
                <div className="space-y-4">
                  <div className="bg-gray-50 p-6 border-2 border-gray-300 min-h-32 flex items-center justify-center">
                    <p className="text-xl text-center text-gray-800 font-medium">"{currentCard}"</p>
                  </div>
                  <div className="flex gap-3 justify-center">
                    <Button 
                      onClick={addToFavorites}
                      variant="outline"
                      className="border-yellow-500 text-yellow-600"
                      style={{ borderRadius: 0 }}
                    >
                      ⭐ Add to Favorites
                    </Button>
                    <Button 
                      onClick={drawCard}
                      className={`${questionSets[cardType].color} text-white`}
                      style={{ borderRadius: 0 }}
                    >
                      <RotateCcw className="w-4 h-4 mr-2" />
                      Draw Another
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <Sparkles className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                  <p className="text-xl mb-4">Ready to deepen connection?</p>
                  <p className="text-base">Draw a card to begin your conversation adventure!</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Favorite Cards */}
          {favoriteCards.length > 0 && (
            <Card className="border-4 border-yellow-500 bg-white" style={{ borderRadius: 0 }}>
              <CardHeader>
                <CardTitle className="text-xl text-yellow-800">⭐ Your Favorite Cards</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 max-h-48 overflow-y-auto">
                  {favoriteCards.map((card, i) => (
                    <div key={i} className="p-3 bg-yellow-50 border-l-4 border-yellow-500">
                      <p className="text-sm text-gray-700">"{card}"</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

        </div>
      </div>
    </div>
  );
}

export default ConnectionCards;